<template>
  <div class="w-full space-y-6">
    <!-- Lista de componentes de upload de áudio em grid de 2 colunas -->
    <TransitionGroup 
      name="audio-grid" 
      tag="div" 
      class="grid grid-cols-1 sm:grid-cols-2 gap-4"
    >
      <div 
        v-for="(audio, index) in audioList" 
        :key="index"
        class="audio-item relative group"
      >
        <!-- Botão de remover (sempre visível no hover) -->
        <button 
          v-if="audioList.length > 1"
          @click="removeAudio(index)" 
          class="absolute -top-2 -right-2 z-10 p-1 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
          title="Remover áudio"
        >
          <XIcon class="w-4 h-4" />
        </button>
        
        <AudioUpload 
          :model-value="audio.file" 
          :title="audio.title"
          :show-title-field="true"
          :initial-preview-url="audio.previewUrl"
          @update:model-value="(file: File) => updateAudio(index, file)"
          @update:title="(title: string) => updateAudioTitle(index, title)"
          @remove="removeAudio(index)"
        />
      </div>
    </TransitionGroup>
    
    <!-- Botão para adicionar mais áudio -->
    <Button
      @click="addAudio" 
      :class="[
        'flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white',
        audioList.length >= 2 ? 'w-full' : 'w-1/2 px-6'
      ]"
    >
      <PlusIcon class="w-5 h-5" />
      Adicionar mais áudio
    </Button>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import { PlusIcon, XIcon } from 'lucide-vue-next'
import AudioUpload from './AudioUpload.vue'
import Button from '../button/Button.vue'
import Label from '../label/Label.vue'
import { toast } from '../toast'
import { Media } from '@/interfaces/Media'

interface AudioItem {
  file: File | null
  title: string
  previewUrl?: string
  mediaId?: number // ID da mídia no servidor
}

interface Props {
  label?: string
  modelValue?: AudioItem[]
  max?: number
  previews?: Media[]
  deleteMediaIds: number[]
}

const props = withDefaults(defineProps<Props>(), {
  label: 'Áudios do bot',
  modelValue: () => [],
  max: 4,
  deleteMediaIds: () => []
})

const emit = defineEmits(['update:modelValue', 'update:deleteMediaIds'])

// Lista de áudios
const audioList = ref<AudioItem[]>([])
// Lista de IDs de mídia para exclusão
const mediaIdsToDelete = ref<number[]>([])

// Inicializar com valores do modelo
watch(() => props.modelValue, (newValue) => {
  if (newValue && newValue.length) {
    audioList.value = [...newValue]
  }
}, { immediate: true })

// Inicializar com previews se disponíveis
onMounted(() => {
  if (props.previews && props.previews.length) {
    // Se já temos audioList com itens, vamos atualizar apenas os que têm previewUrl
    if (audioList.value.length === 0) {
      // Se não temos itens, vamos criar a partir das previews
      audioList.value = props.previews.map(preview => ({
        file: null,
        title: preview.fileOriginalName || '',
        previewUrl: preview.source,
        mediaId: preview.id
      }))
    } else {
      // Atualizar os itens existentes com as informações das previews
      props.previews.forEach((preview, index) => {
        if (index < audioList.value.length) {
          audioList.value[index].previewUrl = preview.source
          audioList.value[index].mediaId = preview.id
          if (!audioList.value[index].title) {
            audioList.value[index].title = preview.fileOriginalName || ''
          }
        } else {
          audioList.value.push({
            file: null,
            title: preview.fileOriginalName || '',
            previewUrl: preview.source,
            mediaId: preview.id
          })
        }
      })
    }
    updateModel()
  }
})

// Se não houver áudios iniciais, adicionar um vazio
if (audioList.value.length === 0) {
  addAudio()
}

// Adicionar um novo áudio à lista
function addAudio() {
  if (audioList.value.length >= props.max) {
    toast({
      title: 'Limite de áudios atingido',
      description: 'Se for necessário mais áudios, entre em contato com o suporte',
      variant: 'destructive',
      duration: 3000
    })
    return
  }
  audioList.value.push({
    file: null,
    title: '',
    previewUrl: ''
  })
  console.log(audioList.value)
  updateModel()
}

// Atualizar o arquivo de um áudio
function updateAudio(index: number, file: File | null) {
  if (index >= 0 && index < audioList.value.length) {
    console.log(index, audioList.value[index])
    const audio = audioList.value[index]
    
    // Se havia uma preview e agora estamos substituindo por um novo arquivo
    // if (audio.previewUrl && audio.mediaId && file) {
    //   // Adicionar o ID da mídia à lista de exclusão
    //   addMediaIdToDelete(audio.mediaId)
    //   // Limpar a referência à preview
    //   audio.previewUrl = ''
    //   audio.mediaId = undefined
    // }
    
    audio.file = file
    console.log()
    // Se o arquivo foi adicionado e não há título, usar o nome do arquivo como título
    if (file && !audio.title) {
      audio.title = file.name.split('.')[0]
    }
    
    updateModel()
  }
}

// Atualizar o título de um áudio
function updateAudioTitle(index: number, title: string) {
  if (index >= 0 && index < audioList.value.length) {
    audioList.value[index].title = title
    updateModel()
  }
}

// Remover um áudio da lista
function removeAudio(index: number) {
  if (index >= 0 && index < audioList.value.length) {
    const audio = audioList.value[index]
    
    // Se o áudio removido tinha uma preview, adicionar o ID à lista de exclusão
    if (audio.mediaId) {
      addMediaIdToDelete(audio.mediaId)
    }
    
    audioList.value.splice(index, 1)
    
    // Se a lista ficar vazia, adicionar um novo áudio vazio
    if (audioList.value.length === 0) {
      addAudio()
    }
    
    updateModel()
  }
}

// Adicionar um ID de mídia à lista de exclusão
function addMediaIdToDelete(mediaId: number) {
  if (!mediaIdsToDelete.value.includes(mediaId)) {
    mediaIdsToDelete.value.push(mediaId)
    // Emitir evento para atualizar o v-model de deleteMediaIds
    emit('update:deleteMediaIds', mediaIdsToDelete.value)
  }
}

// Atualizar o modelo
function updateModel() {
  emit('update:modelValue', audioList.value)
}

// Observar mudanças na lista de IDs para exclusão
watch(mediaIdsToDelete, (newValue) => {
  emit('update:deleteMediaIds', newValue)
}, { deep: true })
</script>

<style scoped>
/* Animações adaptadas para grid */
.audio-grid-enter-active,
.audio-grid-leave-active {
  transition: all 0.3s ease;
}

.audio-grid-enter-from {
  opacity: 0;
  transform: scale(0.9);
}

.audio-grid-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

.audio-grid-move {
  transition: transform 0.3s ease;
}
</style>